import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BellRing, CalendarClock, History, Lock, MessageSquareText, Settings2, Store } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { PageHeader } from '../../../components/ui/PageHeader';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/Tabs';
import useCollectionsSetup from './hooks/useCollectionsSetup';
import useReminderTemplates from './hooks/useReminderTemplates';
import CollectionSettingsTab from './cobranza/CollectionSettingsTab';
import ReminderTemplatesTab from './cobranza/ReminderTemplatesTab';
import ReminderSchedulesTab from './cobranza/ReminderSchedulesTab';
import ReminderDispatchesTab from './cobranza/ReminderDispatchesTab';
import CommerceRulesTab from './cobranza/CommerceRulesTab';

// Pestañas (deep-link ?tab=). Configuración va primero: el plazo es requisito de todo lo demás.
// Plantillas va al lado porque la configuración las asigna a cada paso.
const TABS = [
  { value: 'configuracion', label: 'Configuración', icon: Settings2 },
  { value: 'plantillas', label: 'Plantillas', icon: MessageSquareText },
  { value: 'programados', label: 'Programados', icon: CalendarClock },
  { value: 'historial', label: 'Historial', icon: History },
  { value: 'excepciones', label: 'Excepciones por comercio', icon: Store },
];
const DEFAULT_TAB = TABS[0].value;

/**
 * /admin/cobro/cobranza — vencimientos de las notas y recordatorios de pago a comercios.
 *
 * La configuración del operador se carga una vez acá: la pestaña Configuración la edita y
 * las demás toman de ella el huso horario del operador para mostrar las horas. Las
 * plantillas también: Configuración las asigna y Plantillas las administra (marcar una por
 * defecto cambia la configuración). Al volver a cualquiera de las dos se recarga el
 * catálogo: dónde se usa cada plantilla y cuál es la por defecto los calcula billing con la
 * configuración vigente, que pudo cambiar en la otra pestaña o en otra sesión.
 *
 * El formulario de Configuración vive en su pestaña y cambiar de pestaña lo desmonta: con
 * cambios sin guardar, salir de ella (otra pestaña, "Administrar plantillas" o volver a las
 * notas) pide confirmar antes de descartarlos.
 */
export default function CobranzaView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab = TABS.some((x) => x.value === requested) ? requested : DEFAULT_TAB;
  const batchId = tab === 'historial' ? searchParams.get('batchId') || '' : '';
  const setup = useCollectionsSetup({ withIntegrations: true });
  const templates = useReminderTemplates();
  const timeZone = setup.view?.operatorTimeZone;
  // ¿Configuración tiene cambios sin guardar? En una ref y no en estado: solo la leen los
  // handlers de salida (ningún render depende de ella), así que no hace falta re-renderizar
  // la página cada vez que cambia.
  const settingsDirty = useRef(false);
  const reportSettingsDirty = useCallback((dirty) => {
    settingsDirty.current = dirty;
  }, []);
  // Salida de Configuración en espera de confirmación: { run } o null.
  const [pendingLeave, setPendingLeave] = useState(null);

  const previousTab = useRef(tab);
  const reloadTemplates = templates.reload;
  useEffect(() => {
    if (previousTab.current === tab) return;
    previousTab.current = tab;
    if (tab === 'plantillas' || tab === 'configuracion') reloadTemplates();
  }, [tab, reloadTemplates]);

  const leaveSettings = (run) => {
    if (tab === 'configuracion' && settingsDirty.current) {
      setPendingLeave({ run });
      return;
    }
    run();
  };
  const goTo = (next, extra = {}) => {
    const run = () => setSearchParams({ tab: next, ...extra }, { replace: true });
    if (next === 'configuracion') run();
    else leaveSettings(run);
  };
  const confirmLeave = () => {
    const { run } = pendingLeave;
    setPendingLeave(null);
    settingsDirty.current = false;
    run();
  };

  return (
    <div className="bill-page">
      <button type="button" className="bill-back" onClick={() => leaveSettings(() => navigate('/admin/cobro'))}>
        <ArrowLeft className="w-[18px] h-[18px]" />
        {t('Volver a las notas de cobro')}
      </button>

      <PageHeader
        className="mb-4"
        icon={<BellRing className="w-6 h-6 text-fg-muted" />}
        title={t('Cobranza')}
        subtitle={t('Vencimiento de las notas y recordatorios de pago a los comercios.')}
      />

      {setup.error?.status === 403 ? (
        <EmptyState icon={<Lock />} title={t('Tu rol no tiene permiso para gestionar la cobranza.')} />
      ) : (
        <Tabs value={tab} onValueChange={(next) => goTo(next)}>
          <TabsList ariaLabel={t('Secciones de cobranza')}>
            {TABS.map(({ value, label, icon: Icon }) => (
              <TabsTrigger key={value} value={value} icon={<Icon />}>
                {t(label)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="configuracion" className="mt-4">
            <CollectionSettingsTab
              setup={setup}
              templates={templates}
              onManageTemplates={() => goTo('plantillas')}
              onDirtyChange={reportSettingsDirty}
            />
          </TabsContent>
          <TabsContent value="plantillas" className="mt-4">
            <ReminderTemplatesTab
              templates={templates}
              timeZone={timeZone}
              onSettingsChanged={setup.reload}
              onGoToSettings={() => goTo('configuracion')}
            />
          </TabsContent>
          <TabsContent value="programados" className="mt-4">
            <ReminderSchedulesTab
              timeZone={timeZone}
              onShowDispatches={(id) => goTo('historial', { batchId: id })}
            />
          </TabsContent>
          <TabsContent value="historial" className="mt-4">
            <ReminderDispatchesTab
              key={batchId || 'all'}
              timeZone={timeZone}
              batchId={batchId}
              onClearBatch={() => goTo('historial')}
            />
          </TabsContent>
          <TabsContent value="excepciones" className="mt-4">
            <CommerceRulesTab timeZone={timeZone} />
          </TabsContent>
        </Tabs>
      )}

      <Modal
        open={!!pendingLeave}
        onClose={() => setPendingLeave(null)}
        size="sm"
        title={t('Cambios sin guardar')}
        footer={
          <>
            <Button variant="outline" onClick={() => setPendingLeave(null)}>
              {t('Seguir editando')}
            </Button>
            <Button variant="danger" onClick={confirmLeave}>
              {t('Descartar y salir')}
            </Button>
          </>
        }
      >
        <p className="m-0 text-sm text-fg">
          {t('Tenés cambios sin guardar en la configuración de cobranza. Si salís, se descartan.')}
        </p>
      </Modal>
    </div>
  );
}
